import React, { useEffect, useState, useMemo } from 'react';
import {
  Box,
  Tabs,
  Tab,
  Typography,
  useTheme,
  CircularProgress,
  Stack,
  CardContent,
  Divider,
  CardHeader,
  Avatar,
} from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import { ThunkDispatch } from 'redux-thunk';
import { AnyAction } from 'redux';
import { useNavigate } from 'react-router-dom';

import ChatIcon from '@mui/icons-material/Chat';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import NatureIcon from '@mui/icons-material/Nature';

import { HandymanAppState } from '../../configuration/redux/reducers';
import {
  createOrGetChat,
  getHandymanReceivedProposals,
  updateHandymanProposal,
} from '../Orders/actions';
import { OrdersPageSkeleton } from '../Orders/OrderPageSkeleton';
import {
  GlassButton,
  GlassColors,
  GlassIconButton,
  GlassProposalCard,
  GlassStatusChip,
  NeumorphicButton,
} from '../../components/glassmorphism/GlassmorphismStyles';

const tabSx = (isSelected: boolean, isDark: boolean) => ({
  padding: '4px 24px',
  minHeight: '40px',
  borderRadius: '32px',
  fontFamily: 'Montserrat, sans-serif',
  fontWeight: 500,
  fontSize: '14px',
  textTransform: 'none',
  backdropFilter: 'blur(12px)',
  border: 0,
  transition: 'all .2s ease',

  background: isSelected
    ? isDark
      ? GlassColors.accent
      : GlassColors.secondary
    : isDark
      ? GlassColors.dark.primaryBackground
      : GlassColors.light.primaryBackground,

  boxShadow: `
     0px 0px 16px 0px #5C5C5C4D inset,
      0px 0px 3px 0px #FFFFFF66 inset,
      -1px -1px 0.5px -1px #FFFFFF inset,
      1px 1px 0.5px -1px #FFFFFF66 inset,
      -1px -1px 0px -0.5px #FFFFFF80 inset,
      1px 1px 0px -0.5px #FFFFFF99 inset
  `,

  '&&': {
    color: isSelected
      ? isDark
        ? '#393939'
        : '#E5E5DC'
      : isDark
        ? '#E5E5DC'
        : '#393939',
  },

  '&:active': {
    color: GlassColors.secondary,
    background: isDark
      ? GlassColors.dark.primaryBackground
      : GlassColors.light.primaryBackground,
    boxShadow: `
      0px 0px 3px 0px #00E8E757,
      1px 1px 0px -1px #FFFFFF inset,
      1px 1px 0px -0.5px #E1E1E199,
      0px 1px 4px 0px #0000004D inset,
      0px 0px 2px 0px #0000001A inset
    `,
  },

  '&:hover': {
    background: isSelected
      ? isDark
        ? GlassColors.accent
        : GlassColors.secondary
      : isDark
        ? GlassColors.dark.primaryBackground
        : GlassColors.light.primaryBackground,
    boxShadow: `
      0px 0px 16px 0px #8484844D inset,
      0px 0px 3px 0px #00E8E770 inset,
      -1px -1px 0.5px -1px #FFFFFF inset,
      1px 1px 0.5px -1px #FFFFFFB2 inset,
      -1px -1px 1px -0.5px #FFFFFFA6 inset,
      1px 1px 0px -0.5px #FFFFFF99 inset
    `,
    color: isSelected ? GlassColors.dark.textPrimary : GlassColors.secondary,
  },
});

const ProposalsPage = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const dispatch =
    useDispatch<ThunkDispatch<HandymanAppState, unknown, AnyAction>>();

  const { handymanProposals, handymanProposalsLoading } = useSelector(
    (state: HandymanAppState) => state.order,
  );
  const [updatingProposals, setUpdatingProposals] = useState<string[]>([]);
  const [tab, setTab] = useState(0);

  useEffect(() => {
    dispatch(getHandymanReceivedProposals());
  }, [dispatch]);

  const received = useMemo(() => {
    return handymanProposals.filter(
      (p) => p.status?.toLowerCase() === 'requested',
    );
  }, [handymanProposals]);

  const rejected = useMemo(() => {
    return handymanProposals.filter(
      (p) => p.status?.toLowerCase() === 'rejected',
    );
  }, [handymanProposals]);

  const proposals = tab === 0 ? received : rejected;

  const handleAccept = async (proposalId: string) => {
    await dispatch(updateHandymanProposal(proposalId, 'Confirmed') as any);
    dispatch(getHandymanReceivedProposals() as any);
  };

  const handleReject = async (proposalId: string) => {
    await dispatch(updateHandymanProposal(proposalId, 'Rejected') as any);
    dispatch(getHandymanReceivedProposals() as any);
  };

  const currentUserId = useSelector(
    (state: HandymanAppState) => state.user?.currentUserDetails?.id,
  );
  const currentUserDetails = useSelector(
    (state: HandymanAppState) => state.user?.currentUserDetails,
  );
  const shouldVerify =
    currentUserDetails?.kycStatus !== 'Verified' ||
    currentUserDetails?.isStripeAccountVerified === false;
  // Handle chat with customer (for handyman viewing proposals)
  const handleChatWithCustomer = async (
    e: React.MouseEvent,
    customerId: string,
  ) => {
    e.stopPropagation();

    if (!currentUserId) return;

    try {
      const chatId: any = await dispatch(
        createOrGetChat([currentUserId, customerId]) as any,
      );
      if (chatId) {
        navigate(`/chats/${chatId}`);
      } else {
        console.error('Failed to get chat ID from response');
      }
    } catch (error) {
      console.error('Failed to create or get chat:', error);
    }
  };

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        padding: { xs: '96px 24px 24px', sm: '136px 24px 48px 24px' },
        gap: { xs: '24px', sm: '48px' },
        minHeight: { xs: 'calc(100vh - 336px)', md: 'calc(100vh - 294px)' },
        width: '100%',
        maxWidth: '956px',
        m: 'auto',
        background: isDark ? '#1A1A1A' : '#E5E5DC',
      }}
    >
      {/* Page Title */}
      <Typography
        sx={{
          fontFamily: 'Montserrat, sans-serif',
          fontWeight: 600,
          fontSize: '34px',
          lineHeight: '124%',
          textAlign: 'center',
          letterSpacing: '.25px',
          color: isDark ? GlassColors.accent : GlassColors.secondary,
          width: '100%',
          maxWidth: '956px',
        }}
      >
        My Proposals
      </Typography>

      <Tabs
        value={tab}
        onChange={(e, v) => setTab(v)}
        sx={{
          mb: 3,
          minHeight: '40px',
          '& .MuiTabs-indicator': { display: 'none' },
          '& .MuiTabs-flexContainer': { gap: '16px' },
        }}
      >
        <Tab
          label={`Received (${received.length})`}
          sx={tabSx(tab === 0, isDark)}
        />
        <Tab
          label={`Rejected (${rejected.length})`}
          sx={tabSx(tab === 1, isDark)}
        />
      </Tabs>

      {handymanProposalsLoading ? (
        <OrdersPageSkeleton />
      ) : proposals.length === 0 ? (
        <Typography>No proposals</Typography>
      ) : (
        proposals.map((proposal) => {
          // const disableActions =
          //   proposal.status?.toLowerCase() !== 'requested' ||
          //   updatingProposals.includes(proposal.id!);

          return (
            <GlassProposalCard
              key={proposal.id}
              className={proposal.status?.toLowerCase() || ''}
              sx={{ width: '100%' }}
            >
              <CardHeader
                avatar={
                  <Avatar
                    src={
                      proposal.order?.customer?.avatarUrl ||
                      'https://via.placeholder.com/40'
                    }
                    alt={
                      proposal.order?.customer
                        ? `${proposal.order.customer.firstName} ${proposal.order.customer.lastName}`
                        : 'Customer'
                    }
                    sx={{
                      width: 42,
                      height: 42,
                      border: '3px solid rgba(255, 255, 255, 0.3)',
                      boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
                      transition: 'all 0.3s ease',
                      '&:hover': {
                        transform: 'scale(1.05)',
                        boxShadow: '0 6px 16px rgba(0, 0, 0, 0.2)',
                      },
                    }}
                  />
                }
                title={
                  <Box
                    display='flex'
                    alignItems='center'
                    gap={1.5}
                    sx={{ position: 'relative', zIndex: 1 }}
                  >
                    <Typography
                      variant='h6'
                      sx={{
                        fontSize: '20px',
                        fontWeight: 800,
                        color: isDark ? '#B6B6B6' : '#1A1A1A',
                        fontFamily: 'Montserrat',
                        lineHeight: '160%',
                        letterSpacing: '0.15px',
                      }}
                    >
                      {proposal.order?.name}
                    </Typography>
                    <GlassStatusChip
                      label={proposal.status}
                      size='small'
                      status={proposal.status?.toLowerCase() || ''}
                    />
                  </Box>
                }
                subheader={
                  <Typography
                    variant='body2'
                    sx={{
                      color: isDark ? '#D5D5D5' : '#585858',
                      fontFamily: 'Roboto',
                      fontWeight: 700,
                      fontSize: '16px',
                      lineHeight: '175%',
                      letterSpacing: '0.15px',
                    }}
                  >
                    {proposal.order?.customer
                      ? `${proposal.order.customer.firstName} ${proposal.order.customer.lastName}`
                      : 'Unknown Customer'}
                  </Typography>
                }
                sx={{
                  position: 'relative',
                  zIndex: 1,
                }}
              />
              <Divider sx={{ backgroundColor: 'rgba(255, 255, 255, 0.2)' }} />
              <CardContent sx={{ position: 'relative', zIndex: 1 }}>
                <Box display='flex' flexDirection='column' gap={2.5}>
                  <Typography
                    variant='body1'
                    sx={{
                      color: isDark ? '#B6B6B6' : '#1A1A1A',
                      fontFamily: 'Roboto',
                      lineHeight: '150%',
                      fontSize: '16px',
                      letterSpacing: '0.15px',
                    }}
                  >
                    {proposal.order?.description || 'No description provided'}
                  </Typography>

                  <Stack
                    direction='row'
                    spacing={2.5}
                    alignItems='center'
                    flexWrap='wrap'
                  >
                    {proposal.order?.price && (
                      <Box display='flex' alignItems='center' gap={0.5}>
                        <Box sx={{ display: 'inline' }} />
                        <Typography
                          sx={{
                            fontFamily: 'Roboto',
                            fontWeight: 700,
                            fontSize: '16px',
                            lineHeight: '175%',
                            letterSpacing: '0.15px',
                            color: isDark ? '#00FFFF' : '#005CAD',
                          }}
                        >
                          £{proposal.order.price}
                        </Typography>
                      </Box>
                    )}
                    {proposal.order?.dueDateUtc && (
                      <Box display='flex' alignItems='center' gap={0.5}>
                        <AccessTimeIcon
                          sx={{
                            width: '24px',
                            height: '24px',
                            color: isDark ? '#00FFFF' : '#005CAD',
                          }}
                        />
                        <Typography
                          sx={{
                            fontFamily: 'Roboto',
                            fontWeight: 400,
                            fontSize: '16px',
                            lineHeight: '150%',
                            letterSpacing: '0.15px',
                            color: isDark ? '#B6B6B6' : '#1A1A1A',
                          }}
                        >
                          Due:{' '}
                          {new Date(proposal.order.dueDateUtc).toLocaleString()}
                        </Typography>
                      </Box>
                    )}
                    {proposal.order?.category?.name && (
                      <Box display='flex' alignItems='center' gap={0.5}>
                        <NatureIcon
                          sx={{
                            width: '24px',
                            height: '24px',
                            color: isDark ? '#00FFFF' : '#005CAD',
                          }}
                        />
                        <Typography
                          sx={{
                            fontFamily: 'Roboto',
                            fontWeight: 400,
                            fontSize: '16px',
                            lineHeight: '150%',
                            letterSpacing: '0.15px',
                            color: isDark ? '#B6B6B6' : '#1A1A1A',
                          }}
                        >
                          {proposal.order.category.name}
                        </Typography>
                      </Box>
                    )}
                    {proposal.order?.location && (
                      <Box display='flex' alignItems='center' gap={0.5}>
                        <LocationOnIcon
                          sx={{
                            width: '24px',
                            height: '24px',
                            color: isDark ? '#00FFFF' : '#005CAD',
                          }}
                        />
                        <Typography
                          sx={{
                            fontFamily: 'Roboto',
                            fontWeight: 400,
                            fontSize: '16px',
                            lineHeight: '150%',
                            letterSpacing: '0.15px',
                            color: isDark ? '#B6B6B6' : '#1A1A1A',
                          }}
                        >
                          {proposal.order.location}
                        </Typography>
                      </Box>
                    )}
                  </Stack>

                  <Box
                    display='flex'
                    justifyContent='space-between'
                    alignItems='center'
                    gap={2}
                  >
                    <Box display='flex' alignItems='center' gap={1.5}>
                      <GlassIconButton
                        onClick={(e) => {
                          const customerId = proposal.order?.customer?.id;
                          if (customerId) {
                            handleChatWithCustomer(e, customerId);
                          }
                        }}
                        title='Chat with Customer'
                        sx={{
                          color: '#005CAD',
                          '&:hover': {
                            backgroundColor: 'rgba(0, 92, 173, 0.1)',
                            transform: 'scale(1.1)',
                          },
                        }}
                      >
                        <ChatIcon />
                      </GlassIconButton>
                      {proposal.status?.toLowerCase() === 'requested' && (
                        <>
                          <GlassButton
                            variant='outlined'
                            onClick={async () => {
                              if (shouldVerify) {
                                navigate('/verification');
                                return;
                              }
                              setUpdatingProposals((prev) => [
                                ...prev,
                                proposal.id!,
                              ]);
                              try {
                                await handleAccept(proposal.id!);
                              } finally {
                                setUpdatingProposals((prev) =>
                                  prev.filter((id) => id !== proposal.id),
                                );
                              }
                            }}
                            disabled={updatingProposals.includes(proposal.id!)}
                          >
                            {updatingProposals.includes(proposal.id!) ? (
                              <CircularProgress size={20} color='inherit' />
                            ) : shouldVerify ? (
                              'Verify to Accept'
                            ) : (
                              'Accept'
                            )}
                          </GlassButton>

                          <GlassButton
                            variant='outlined'
                            onClick={async () => {
                              setUpdatingProposals((prev) => [
                                ...prev,
                                proposal.id!,
                              ]);
                              try {
                                await handleReject(proposal.id!);
                              } finally {
                                setUpdatingProposals((prev) =>
                                  prev.filter((id) => id !== proposal.id),
                                );
                              }
                            }}
                            disabled={updatingProposals.includes(proposal.id!)}
                          >
                            {updatingProposals.includes(proposal.id!) ? (
                              <CircularProgress size={20} color='inherit' />
                            ) : (
                              'Reject'
                            )}
                          </GlassButton>
                        </>
                      )}
                    </Box>
                    <NeumorphicButton
                      onClick={() => navigate(`/orders/${proposal.order?.id}`)}
                      sx={{
                        padding: '8px 24px',
                        height: '48px',
                        minWidth: '120px',
                      }}
                    >
                      View Details
                    </NeumorphicButton>
                  </Box>
                </Box>
              </CardContent>
            </GlassProposalCard>
          );
        })
      )}
    </Box>
  );
};

export default ProposalsPage;
